import { Gender, GenderSchema, GenderType } from "@garther/interfaces";
import { Avatar, Box, Button, Spinner } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { PenLine, Save, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { profileApiClient } from "@/libs/api";
import { useAuthStore } from "@/stores/auth.store";

interface BasicInfoProps {
  userId: string;
  isOwner: boolean;
}

interface LocalUserProfile {
  name: string;
  email: string;
  gender: GenderType;
  dateOfBirth: string;
  phone: string;
}

// Age validation utility
const validateAge = (dateString: string): string | undefined => {
  if (!dateString) return;

  const birthDate = new Date(dateString);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust age if birthday hasn't occurred this year
  const actualAge =
    monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

  if (actualAge > 100) return "Tuổi của bạn vượt quá 100 tuổi";
  if (actualAge < 0) return "Ngày sinh không thể là ngày trong tương lai";
  if (actualAge < 16) return "Bạn chưa đủ 16 tuổi";

  return;
};

// Form field configuration types
type FormFieldConfig = {
  name: keyof LocalUserProfile;
  label: string;
  type: "text" | "date" | "select";
  editable: boolean;
  placeholder?: string;
  options?: Array<{ value: GenderType; label: string }>;
  validator?: (value: string) => string | undefined;
};

const FORM_FIELDS: FormFieldConfig[] = [
  {
    name: "name",
    label: "profile.basicInfo.username",
    type: "text",
    editable: true,
    placeholder: "profile.basicInfo.enterYourName"
  },
  {
    name: "gender",
    label: "profile.basicInfo.gender",
    type: "select",
    editable: true,
    options: [
      { value: Gender.MALE, label: "profile.creation.genderOptions.male" },
      { value: Gender.FEMALE, label: "profile.creation.genderOptions.female" },
      { value: Gender.OTHER, label: "profile.creation.genderOptions.other" },
      { value: Gender.PREFER_NOT_TO_SAY, label: "profile.creation.genderOptions.preferNotToSay" }
    ]
  },
  {
    name: "dateOfBirth",
    label: "profile.basicInfo.dateOfBirth",
    type: "date",
    editable: true,
    validator: validateAge
  },
  {
    name: "phone",
    label: "profile.basicInfo.phone",
    type: "text",
    editable: true,
    placeholder: "profile.basicInfo.enterYourPhone"
  }
];

const defaultValues: LocalUserProfile = {
  name: "",
  email: "",
  gender: Gender.PREFER_NOT_TO_SAY,
  dateOfBirth: "",
  phone: ""
};

export default function BasicInfo({ userId, isOwner }: BasicInfoProps) {
  const { curUser, updateProfile, setProfile } = useAuthStore();
  const { t, formatDate } = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalValues, setOriginalValues] = useState<LocalUserProfile>(defaultValues);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (!userId || !isOwner) return;

      setError(null);

      try {
        await updateProfile({ ...value, gender: GenderSchema.parse(value.gender) });
        setOriginalValues(value);
        setIsEditing(false);
      } catch (err) {
        console.error("Error saving profile:", err);
        setError(t("profile.basicInfo.failedToSaveProfile"));
      }
    }
  });

  // Helper function to set all form fields
  const setFormFields = useCallback(
    (data: LocalUserProfile) => {
      Object.entries(data).forEach(([key, value]) => {
        form.setFieldValue(key as keyof LocalUserProfile, value);
      });
    },
    [form]
  );

  // Helper function to restore original values
  const restoreOriginalValues = useCallback(() => {
    setFormFields(originalValues);
  }, [originalValues, setFormFields]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) return;

      setIsLoading(true);
      setError(null);

      try {
        const firebaseProfile = isOwner ? curUser.profile : await profileApiClient.getProfileByUserId(userId);

        const profileData = firebaseProfile
          ? {
              name: firebaseProfile.name,
              email: firebaseProfile.email,
              gender: firebaseProfile.gender,
              dateOfBirth: firebaseProfile.dateOfBirth,
              phone: firebaseProfile.phone ?? ""
            }
          : {
              name: curUser?.fbUser?.displayName || "-",
              email: curUser?.fbUser?.email || "-",
              gender: Gender.PREFER_NOT_TO_SAY,
              dateOfBirth: "",
              phone: curUser?.fbUser?.phoneNumber ?? ""
            };

        setOriginalValues(profileData);
        setFormFields(profileData);
      } catch (err) {
        console.error("Error loading profile:", err);
        setError(t("profile.basicInfo.failedToLoadProfile"));
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [userId, isOwner, curUser, setFormFields, t]);

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsEditing(false);
      setError(null);
      restoreOriginalValues();
    },
    [restoreOriginalValues]
  );

  const handleEdit = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsEditing(true);
  }, []);

  const handleAvatarClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // notImplementedToast();
      if (isOwner && fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    [isOwner]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError(t("profile.basicInfo.invalidFileType"));
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError(t("profile.basicInfo.fileTooLarge"));
        return;
      }

      setIsUploadingAvatar(true);
      setError(null);

      try {
        const updatedProfile = await profileApiClient.uploadAvatar(file);
        setProfile(updatedProfile);
      } catch (err) {
        console.error("Error uploading avatar:", err);
        setError(t("profile.basicInfo.failedToUploadAvatar"));
      } finally {
        setIsUploadingAvatar(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [t, setProfile]
  );

  // Render form field
  const renderField = (fieldConfig: FormFieldConfig) => {
    const fieldName = fieldConfig.name;
    const isEditable = fieldConfig.editable && isEditing && isOwner;
    const fieldValue = form.getFieldValue(fieldName);

    if (!isEditable) {
      const displayValue =
        fieldName === "dateOfBirth" && fieldValue
          ? formatDate(new Date(fieldValue), { year: "numeric", month: "short", day: "numeric" })
          : fieldName === "gender"
            ? t(`profile.creation.genderOptions.${fieldValue.toLowerCase()}`)
            : fieldValue || t("profile.basicInfo.notProvided");

      return <span className="text-textcolor-primary text-responsive-base">{displayValue}</span>;
    }

    // Create validator based on field type and config
    const getValidator = () => {
      // Add name validation
      if (fieldName === "name") {
        return {
          onChange: ({ value }: { value: string }) => {
            const trimmedValue = value?.trim() || "";
            if (!trimmedValue) return t("profile.creation.errors.nameRequired");
            if (trimmedValue.length < 3) return t("profile.creation.errors.nameMinLength");
            if (trimmedValue.length > 32) return t("profile.creation.errors.nameMaxLength");
            return undefined;
          }
        };
      }

      // Add phone validation
      if (fieldName === "phone") {
        return {
          onChange: ({ value }: { value: string }) => {
            if (!value) return undefined; // Phone is optional in basic info
            const trimmedValue = value.trim();
            // Check if phone contains only digits, spaces, hyphens, and parentheses
            if (!/^[\d\s\-()+]+$/.test(trimmedValue)) {
              return t("profile.creation.errors.phoneInvalid");
            }
            // Check minimum length (at least 8 digits)
            const digitCount = trimmedValue.replace(/\D/g, "").length;
            if (digitCount > 0 && digitCount < 8) {
              return t("profile.creation.errors.phoneMinLength");
            }
            // Check maximum length (max 15 digits, international format)
            if (digitCount > 15) {
              return t("profile.creation.errors.phoneMaxLength");
            }
            return undefined;
          }
        };
      }

      // Add date of birth validation (with translations)
      if (fieldName === "dateOfBirth") {
        return {
          onChange: ({ value }: { value: string }) => {
            if (!value) return undefined; // Date of birth is optional in basic info
            const birthDate = new Date(value);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            // Adjust age if birthday hasn't occurred this year
            const actualAge =
              monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

            if (actualAge > 100) return t("profile.creation.errors.ageOver100");
            if (actualAge < 0) return t("profile.creation.errors.futureDate");
            if (actualAge < 16) return t("profile.creation.errors.ageBelow16");

            return undefined;
          }
        };
      }

      // Fallback to fieldConfig validator if provided
      if (fieldConfig.validator) {
        return { onChange: ({ value }: { value: string }) => fieldConfig.validator!(value) };
      }

      return undefined;
    };

    return (
      <form.Field name={fieldName} validators={getValidator()}>
        {(field) => (
          <div className="flex flex-col w-[40%] min-w-[200px]">
            {fieldConfig.type === "select" ? (
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(GenderSchema.parse(e.target.value))}
                className="text-responsive-base text-textcolor-primary bg-transparent border-b border-border-light focus:outline-none focus:border-border-dark focus:bg-background-highlight hover:cursor-pointer hover:bg-background-highlight"
                disabled={form.state.isSubmitting}
              >
                {fieldConfig.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={fieldConfig.type}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-responsive-base text-textcolor-primary bg-transparent border-b border-border-light focus:outline-none focus:border-border-dark focus:bg-background-highlight hover:cursor-pointer hover:bg-background-highlight"
                placeholder={fieldConfig.placeholder ? t(fieldConfig.placeholder) : undefined}
                disabled={form.state.isSubmitting}
              />
            )}
            {field.state.meta.errors && (
              <span className="text-red-500 text-xs mt-1">{field.state.meta.errors.join(", ")}</span>
            )}
          </div>
        )}
      </form.Field>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-textcolor-secondary mt-2">{t("profile.basicInfo.loadingProfile")}</p>
        </div>
      </div>
    );
  }

  if (!curUser.profile) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-textcolor-secondary mt-2">{t("profile.basicInfo.cannotLoadProfile")}</p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="px-4 md:px-6 py-2">
        <p className="text-text-secondary text-responsive-lg font-normal">
          {t("profile.basicInfo.aboutYou")}
        </p>
      </div>
      <Box className="flex flex-col justify-stretch items-start gap-4">
        <div className="flex justify-start items-center gap-6 w-full">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          {/* <button className="relative" onClick={handleAvatarClick}> */}
          <div className="relative">
            <Avatar
              name={curUser.profile.name ?? undefined}
              src={curUser.profile.avatar ?? undefined}
              size="2xl"
            />
            <Button
              variant="ghost"
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="text-text-secondary !p-1.5 absolute bottom-0 right-0 bg-background-secondary border border-border rounded-full"
            >
              {isUploadingAvatar ? <Spinner className="w-3 h-3" /> : <PenLine className="w-3 h-3" />}
            </Button>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start flex-1 gap-2">
            <div className="flex flex-col justify-center items-start">
              <p className="text-text-primary text-responsive-md font-normal">{curUser.profile.email}</p>
              <p className="text-text-secondary text-responsive-base font-normal">
                {t("profile.basicInfo.joinedOn") +
                  " " +
                  formatDate(curUser.profile.createdAt, { year: "numeric", month: "short", day: "numeric" })}
              </p>
            </div>
            {isOwner && (
              <div className="flex flex-col justify-center items-end">
                <div className="flex justify-center items-center space-x-2 h-full">
                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        type="submit"
                        isDisabled={form.state.isSubmitting}
                        className="text-text-primary !px-2 !py-1"
                      >
                        {form.state.isSubmitting ? (
                          <Spinner className="w-4 h-4" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span className="text-responsive-base ml-2">{t("profile.basicInfo.save")}</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        isDisabled={form.state.isSubmitting}
                        className="text-text-secondary !px-2 !py-1 border-border"
                      >
                        <X className="w-4 h-4" />
                        <span className="text-responsive-base ml-2">{t("profile.basicInfo.cancel")}</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleEdit}
                      className="text-text-secondary border-border !px-2 !py-1"
                    >
                      <PenLine className="w-4 h-4" />
                      <span className="text-responsive-base ml-2">{t("profile.basicInfo.editProfile")}</span>
                    </Button>
                  )}
                </div>
                {error && (
                  <div className="mt-2 text-red-600 w-full text-responsive-base text-end">{error}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 w-full">
          <div className="space-y-2">
            {FORM_FIELDS.map((fieldConfig) => (
              <div
                key={fieldConfig.name}
                className="flex justify-between items-center py-1 border-b border-border-light"
              >
                <span className="text-text-secondary text-responsive-base">{t(fieldConfig.label)}</span>
                {renderField(fieldConfig)}
              </div>
            ))}
          </div>
        </div>
      </Box>
    </form>
  );
}
